// Clyde threads on the admin side — the one place the account list, the account
// page and the "Start a Clyde thread" dialog agree on paths, keys, phone display
// and what each server refusal means in plain words.

/** GET /agent/threads — every live thread in the org, bounded server-side. */
export const AGENT_THREADS_PATH = "/api/agent/threads";

/** What GET /agent/threads lists per thread (server/routes.ts). */
export interface AgentThreadSummary {
  id: number;
  accountId: number | null;
  agentConversationId: number;
  channel: "sms" | "whatsapp";
  repAddress: string | null;
  repName: string | null;
  createdAt: string;
  lastInboundAt: string | null;
}

/** TanStack key for one account's thread (GET /accounts/:id/agent-thread). */
export function accountThreadKey(accountId: number | string): (string | number)[] {
  return ["/api/accounts", accountId, "agent-thread"];
}

// ---- Orientation in the Start dialog (server/services/clydeOrientation.ts) ----

/** What GET /accounts/:id/agent-thread/orientation lists per word. */
export interface OrientationQuestionView {
  word: string;
  candidates: Array<{ productId: number; name: string }>;
}

/** The dialog's picks: a product id per word, or "depends". */
export type OrientationAnswers = Record<string, number | "depends">;

/** After this long without questions, the dialog offers to start and have Clyde text the rep instead. */
export const ORIENTATION_SLOW_MS = 60_000;

export function orientationKey(accountId: number | string): (string | number)[] {
  return ["/api/accounts", accountId, "agent-thread", "orientation"];
}

/** The POST body's `orientation`: every question, its pick or null for "depends". */
export function orientationAnswersBody(questions: OrientationQuestionView[], answers: OrientationAnswers): Array<{ word: string; productId: number | null }> {
  return questions.map((q) => {
    const a = answers[q.word];
    return { word: q.word, productId: typeof a === "number" && q.candidates.some((c) => c.productId === a) ? a : null };
  });
}

/** The console, opened on one conversation (oversight-console.tsx reads `?conversation=`). */
export function consoleThreadPath(adminBase: string, agentConversationId: number): string {
  return `${adminBase}/agent?conversation=${agentConversationId}`;
}

/** The console on one conversation, scrolled to one message (`?message=` is optional). */
export function consoleMessagePath(adminBase: string, agentConversationId: number, messageId: number | null | undefined): string {
  const base = consoleThreadPath(adminBase, agentConversationId);
  return messageId ? `${base}&message=${messageId}` : base;
}

/** The orders page with one order's lightbox open (seller-dashboard.tsx reads `?order=`). */
export function ordersPageOrderPath(adminBase: string, orderId: number): string {
  return `${adminBase}/orders?order=${orderId}`;
}

/** "+14165550100" or "4165550100" → "416-555-0100"; anything else as given. */
export function displayPhone(value: string | null | undefined): string {
  const raw = (value ?? "").replace(/^whatsapp:/i, "");
  const digits = raw.replace(/\D/g, "");
  const ten = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (ten.length === 10) return `${ten.slice(0, 3)}-${ten.slice(3, 6)}-${ten.slice(6)}`;
  return raw;
}

/** What apiRequest's `"<status>: <body>"` error carried. */
export interface ApiErrorInfo {
  status: number | null;
  code: string | null;
  message: string;
  body: Record<string, unknown> | null;
}

export function parseApiError(err: unknown): ApiErrorInfo {
  const text = err instanceof Error ? err.message : String(err ?? "");
  const m = text.match(/^(\d{3}):\s*([\s\S]*)$/);
  if (!m) return { status: null, code: null, message: text, body: null };
  const status = parseInt(m[1], 10);
  try {
    const body = JSON.parse(m[2]) as Record<string, unknown>;
    return {
      status,
      code: typeof body.code === "string" ? body.code : null,
      message: typeof body.message === "string" ? body.message : m[2],
      body,
    };
  } catch {
    return { status, code: null, message: m[2], body: null };
  }
}

/**
 * Plain words for each refusal `POST /accounts/:id/agent-thread` can answer
 * (ClydeThreadError codes in server/services/clydeThreads.ts, plus the route's
 * own THREAD_EXISTS). A code we don't know falls back to the server's message, and
 * the dialog always shows something — never a silent failure.
 */
export function clydeThreadErrorMessage(info: ApiErrorInfo): string {
  switch (info.code) {
    case "GROUP_ACCOUNT":
      return "Group accounts can't order by text yet. Start the thread on a standalone account instead.";
    case "ACCOUNT_NOT_FOUND":
      return "That account isn't in this organization. Refresh the account list and try again.";
    case "CONTACT_NOT_FOUND":
      return "That contact is no longer on this account. Close the dialog and try again.";
    case "BUYER_IN_THREAD":
      return "That number is already on another Clyde thread, so it can't start a second one.";
    case "THREAD_EXISTS": {
      // One live thread per account (Jon, 2026-10-20): name who it is with.
      const thread = info.body?.thread as { buyerAddress?: string } | undefined;
      return thread?.buyerAddress
        ? `This account already has a Clyde thread with ${displayPhone(thread.buyerAddress)}.`
        : "This account already has a Clyde thread.";
    }
    case "TRANSPORT_NOT_CONFIGURED":
      return "Texting isn't set up on this server yet (no Twilio Conversations credentials). Contact FreshOrder.";
    case "NO_AGENT_NUMBER":
      return "This organization has no Clyde phone number yet. Contact FreshOrder to set one up.";
    case "WHATSAPP_NOT_READY":
      return "WhatsApp threads aren't available yet. Choose Text.";
    case "DEMO_ORG":
      return "The demo portal never texts anyone.";
    case "NO_PHONE":
      return "This account has no phone number. Add one on the account page first.";
    case "INVALID_PHONE":
      return info.message || "One of the phone numbers isn't a valid number.";
    case "TWILIO_FAILED":
      return info.message || "Twilio refused the thread. Try again in a minute.";
    default:
      if (info.status === 401) return "Your admin session has expired. Sign in again.";
      return info.message || "Couldn't start the Clyde thread.";
  }
}
